import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import {
  TrendingUpRounded as TrendingUp,
  TrendingDownRounded as TrendingDown,
  MoneyRounded as Banknote,
  MonetizationOnRounded as MoneyBag,
} from '@mui/icons-material'
import { formatNumber } from '../../utils/numbersFormatter'
import { tokens } from '../../styles/tokens'
import { useCardHeaderStyles } from '../../styles/cards'
import { CouponPayment, CurrencyAmount, ProfitLossDeal } from '../../redux/reducers/clients'

const useStyles = makeStyles(theme => ({
  heroBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingTop: theme.spacing(1),
  },
  heroLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: tokens.color.neutral[500],
    margin: 0,
  },
  heroLabelNote: {
    fontSize: '0.6875rem',
    fontStyle: 'italic',
  },
  heroValue: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: tokens.color.neutral[900],
    fontVariantNumeric: 'tabular-nums',
    margin: 0,
  },
  statsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    borderTop: `1px solid ${tokens.color.neutral[100]}`,
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: tokens.radius.md,
    flexShrink: 0,
    '& svg': {
      fontSize: '1.05rem',
    },
  },
  statIconPositive: {
    backgroundColor: tokens.color.success[100],
    color: tokens.color.success[700],
  },
  statIconNegative: {
    backgroundColor: tokens.color.error[100],
    color: tokens.color.error[700],
  },
  statTextWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexGrow: 1,
    minWidth: 0,
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: tokens.color.neutral[500],
    margin: 0,
  },
  statValue: {
    fontSize: '0.875rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  statValuePositive: {
    color: tokens.color.success[700],
  },
  statValueNegative: {
    color: tokens.color.error[500],
  },
  taxSectionsRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    borderTop: `1px solid ${tokens.color.neutral[100]}`,
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
  taxSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    flex: 1,
    minWidth: 0,
  },
  taxSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
  },
  taxSectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: tokens.color.neutral[500],
    margin: 0,
  },
  taxProfitLoss: {
    fontSize: '0.875rem',
    fontWeight: 600,
    margin: 0,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  viewSwitch: {
    display: 'flex',
    borderRadius: tokens.radius.md,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    overflow: 'hidden',
  },
  viewSwitchButton: {
    border: 'none',
    cursor: 'pointer',
    padding: '3px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    color: tokens.color.white,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.18)',
    },
  },
  viewSwitchButtonActive: {
    backgroundColor: tokens.color.white,
    color: tokens.color.primary[800],
    '&:hover': {
      backgroundColor: tokens.color.white,
    },
  },
  couponList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(0.5),
  },
  couponRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    borderBottom: `1px solid ${tokens.color.neutral[100]}`,
    paddingBottom: theme.spacing(0.75),
  },
  couponDate: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    margin: 0,
    width: '78px',
    flexShrink: 0,
  },
  couponName: {
    fontSize: '0.75rem',
    color: tokens.color.neutral[500],
    margin: 0,
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  couponAmount: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: tokens.color.success[700],
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  couponEmpty: {
    fontSize: '0.75rem',
    color: tokens.color.neutral[500],
    margin: 0,
    paddingTop: theme.spacing(1),
  },
}))

// Vynosy z obchodovani s CP docasne skryte, dokud vypocet neoveri ucetni.
const SHOW_SECURITIES_SALE = false

interface CashFlowStatsProps {
  thisYear: number
  nextYear: number
  fiveYears: number
  tenYears: number
  securitiesSaleDeals?: ProfitLossDeal[]
  couponsAndDividends?: CurrencyAmount[]
  /** Rozpis nejblizsich vyplat kuponu. Kdyz chybi, prepinac pohledu se nezobrazi. */
  couponPayments?: CouponPayment[]
}

const StatRow = ({
  classes,
  label,
  value,
}: {
  classes: ReturnType<typeof useStyles>
  label: string
  value: number
}) => (
  <div className={classes.statRow}>
    <span className={classNames(classes.statIcon, value >= 0 ? classes.statIconPositive : classes.statIconNegative)}>
      {value >= 0 ? <TrendingUp /> : <TrendingDown />}
    </span>
    <div className={classes.statTextWrapper}>
      <p className={classes.statLabel}>{label}</p>
      <p className={classNames(classes.statValue, value >= 0 ? classes.statValuePositive : classes.statValueNegative)}>
        {formatNumber(value)} CZK
      </p>
    </div>
  </div>
)

const CouponSchedule = ({
  classes,
  payments,
}: {
  classes: ReturnType<typeof useStyles>
  payments: CouponPayment[]
}) => {
  const { t } = useTranslation()

  if (payments.length === 0) {
    return <p className={classes.couponEmpty}>{t('CASHFLOW_NO_UPCOMING_COUPONS')}</p>
  }

  return (
    <div className={classes.couponList}>
      {payments.map((payment, index) => (
        <div key={`${payment.securityId}-${payment.date}-${index}`} className={classes.couponRow}>
          <p className={classes.couponDate}>{new Date(payment.date).toLocaleDateString('cs-CZ')}</p>
          <p className={classes.couponName} title={payment.name}>
            {payment.name}
          </p>
          <p className={classes.couponAmount}>
            + {formatNumber(payment.amount)} {payment.currency}
          </p>
        </div>
      ))}
    </div>
  )
}

const CashFlowStats: React.FC<CashFlowStatsProps> = ({
  thisYear,
  nextYear,
  fiveYears,
  tenYears,
  securitiesSaleDeals,
  couponsAndDividends,
  couponPayments,
}: CashFlowStatsProps) => {
  const classes = useStyles()
  const cardHeader = useCardHeaderStyles()
  const { t } = useTranslation()
  const [detailView, setDetailView] = useState(false)

  const hasDetailView = couponPayments !== undefined

  return (
    <>
      <div className={cardHeader.header}>
        <p className={cardHeader.title}>{t('CASHFLOW')}</p>
        {hasDetailView && (
          <div className={classes.viewSwitch}>
            <button
              type="button"
              className={classNames(classes.viewSwitchButton, !detailView && classes.viewSwitchButtonActive)}
              onClick={() => setDetailView(false)}
            >
              {t('CASHFLOW_VIEW_TOTAL')}
            </button>
            <button
              type="button"
              className={classNames(classes.viewSwitchButton, detailView && classes.viewSwitchButtonActive)}
              onClick={() => setDetailView(true)}
            >
              {t('CASHFLOW_VIEW_DETAIL')}
            </button>
          </div>
        )}
      </div>
      {hasDetailView && detailView ? (
        <>
          <div className={classes.heroBlock}>
            <p className={classes.heroLabel}>{t('CASHFLOW_UPCOMING_COUPONS')}</p>
          </div>
          <CouponSchedule classes={classes} payments={couponPayments || []} />
        </>
      ) : (
        <>
          <div className={classes.heroBlock}>
            <p className={classes.heroLabel}>
              {t('PORTFOLIO_INCOME_CASHFLOW_TY')} <span className={classes.heroLabelNote}>{t('PORTFOLIO_INCOME_CASHFLOW_TY_NOTE')}</span>
            </p>
            <p className={classes.heroValue}>{formatNumber(thisYear)} CZK</p>
          </div>
          <div className={classes.statsSection}>
            <StatRow classes={classes} label={t('PORTFOLIO_INCOME_CASHFLOW_NY')} value={nextYear} />
            <StatRow classes={classes} label={t('PORTFOLIO_INCOME_CASHFLOW_5Y')} value={fiveYears} />
            <StatRow classes={classes} label={t('PORTFOLIO_INCOME_CASHFLOW_10Y')} value={tenYears} />
          </div>
        {((SHOW_SECURITIES_SALE && securitiesSaleDeals && securitiesSaleDeals.length > 0) || (couponsAndDividends && couponsAndDividends.length > 0)) && (
          <div className={classes.taxSectionsRow}>
            {couponsAndDividends && couponsAndDividends.length > 0 && (
              <div className={classes.taxSection}>
                <div className={classes.taxSectionHeader}>
                  <span className={classNames(classes.statIcon, classes.statIconPositive)}>
                    <Banknote />
                  </span>
                  <p className={classes.taxSectionTitle}>{t('CASHFLOW_COUPONS_DIVIDENDS_TITLE')}</p>
                </div>
                {couponsAndDividends.map((item, index) => (
                  <p key={index} className={classNames(classes.taxProfitLoss, classes.statValuePositive)}>
                    + {formatNumber(item.amount)} {item.currency}
                  </p>
                ))}
              </div>
            )}
            {SHOW_SECURITIES_SALE && securitiesSaleDeals && securitiesSaleDeals.length > 0 && (
              <div className={classes.taxSection}>
                <div className={classes.taxSectionHeader}>
                  <span className={classNames(classes.statIcon, classes.statIconPositive)}>
                    <MoneyBag />
                  </span>
                  <p className={classes.taxSectionTitle}>{t('CASHFLOW_SECURITIES_SALE_TITLE')}</p>
                </div>
                {securitiesSaleDeals.map((deal, index) => {
                  const isProfit = deal.profitLoss >= 0
                  return (
                    <p
                      key={index}
                      className={classNames(classes.taxProfitLoss, isProfit ? classes.statValuePositive : classes.statValueNegative)}
                    >
                      {isProfit ? '+' : '-'} {formatNumber(Math.abs(deal.profitLoss))} {deal.currency}
                    </p>
                  )
                })}
              </div>
            )}
          </div>
          )}
        </>
      )}
    </>
  )
}

export default CashFlowStats
